import { useEffect, useMemo, useState } from 'react'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import { useWorkspace } from '../../context/useWorkspace'
import { buildFieldPassUrl, createFieldPass } from '../../services/fieldPassService'
import { createSchoolFieldPass, type SchoolEduScope } from '../../services/eduFieldPassSchoolService'
import { listSeriesForWorkspace, type EduSessionSeries } from '../../services/eduTimetableService'
import type { FieldPassRecord } from '../../types/fieldPass'
import { Radio, TextInput } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { BottomSheet } from '../Common/BottomSheet'
import { SheetSelect } from '../Common/SheetSelect'
import { Toggle } from '../Common/Toggle'

export type QuickDelegateTarget = {
  id: string
  title: string
}

type QuickDelegateResult = {
  pass: FieldPassRecord
  token: string
  url: string
}

type QuickDelegateSheetProps = {
  open: boolean
  workspaceId: string
  targets: QuickDelegateTarget[]
  defaultExpiresInHours?: number
  onClose: () => void
  onCreated?: (result: QuickDelegateResult, url: string) => void | Promise<void>
  onNotice?: (message: string, tone?: 'success' | 'error') => void
}

const EXPIRY_OPTIONS: Array<{ value: `${number}`; hours: number; labelKey: NlsKey }> = [
  { value: '6', hours: 6, labelKey: 'NLSFieldPassQuickExpires6h' },
  { value: '12', hours: 12, labelKey: 'NLSFieldPassQuickExpires12h' },
  { value: '24', hours: 24, labelKey: 'NLSFieldPassQuickExpires24h' },
  { value: '72', hours: 72, labelKey: 'NLSFieldPassQuickExpires72h' },
]

const copyText = async (value: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(value)
}

export const QuickDelegateSheet = ({
  open,
  workspaceId,
  targets,
  defaultExpiresInHours = 6,
  onClose,
  onCreated,
  onNotice,
}: QuickDelegateSheetProps) => {
  const { t } = useNls()
  let activeRole: string | undefined = undefined
  let workspace: ReturnType<typeof useWorkspace>['workspaces'][number] | undefined = undefined
  try {
    const ws = useWorkspace()
    activeRole = ws.activeRole
    workspace = ws.workspaces.find((w) => w.id === workspaceId)
  } catch {
    // context optional in tests
  }
  const isHelper = activeRole === 'HELPER'
  const isEduWorkspace = Boolean(
    workspace?.modules?.teachingEnabled || workspace?.modules?.trainingEnabled,
  )

  const [mode, setMode] = useState<'CHECKLIST' | 'SCHOOL'>('CHECKLIST')
  const [schoolKind, setSchoolKind] = useState<'SESSION' | 'EXAM' | 'COACH'>('SESSION')
  const [schoolSessionId, setSchoolSessionId] = useState('')
  const [schoolAssessmentId, setSchoolAssessmentId] = useState('')
  const [schoolHall, setSchoolHall] = useState('')
  const [schoolMetricsStr, setSchoolMetricsStr] = useState('')
  const [availableSeries, setAvailableSeries] = useState<EduSessionSeries[]>([])

  const [expiresInHours, setExpiresInHours] = useState(defaultExpiresInHours)
  const [deleteOnCompletion, setDeleteOnCompletion] = useState(false)
  const [targetEmail, setTargetEmail] = useState('')
  const [sendViaWhatsApp, setSendViaWhatsApp] = useState(false)
  const [deliveryPhone, setDeliveryPhone] = useState('')
  const [whatsappNotice, setWhatsappNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastLink, setLastLink] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    void Promise.resolve().then(() => {
      if (cancelled) return
      setExpiresInHours(defaultExpiresInHours)
      setDeleteOnCompletion(false)
      setTargetEmail('')
      setSendViaWhatsApp(false)
      setDeliveryPhone('')
      setWhatsappNotice(null)
      setBusy(false)
      setError(null)
      setLastLink(null)
    })
    return () => {
      cancelled = true
    }
  }, [defaultExpiresInHours, open])

  useEffect(() => {
    if (!open || !workspaceId || isHelper || !isEduWorkspace || mode !== 'SCHOOL') return
    let active = true
    listSeriesForWorkspace(workspaceId)
      .then((res) => {
        if (!active) return
        setAvailableSeries(res || [])
        if (res?.length && !schoolSessionId) {
          setSchoolSessionId(res[0].id || '')
        }
      })
      .catch((err) => {
        console.warn('Failed to load series for quick delegate school pass', err)
      })
    return () => {
      active = false
    }
  }, [open, workspaceId, isHelper, isEduWorkspace, mode, schoolSessionId])

  const now = new Date()
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  const hoursUntilMidnight = Math.max(1, (midnight.getTime() - now.getTime()) / 3600000)
  const clampedSchoolHours = Math.min(12, Math.floor(hoursUntilMidnight))
  const schoolEndTime = new Date(now.getTime() + clampedSchoolHours * 3600000)
  const schoolEndTimeStr = schoolEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  const cleanTargets = useMemo(() => {
    const seen = new Set<string>()
    return targets
      .map((target) => ({ id: target.id.trim(), title: target.title.trim() || t('NLSFieldPassQuickUntitledTarget') }))
      .filter((target) => {
        if (!target.id || seen.has(target.id)) return false
        seen.add(target.id)
        return true
      })
      .slice(0, 8)
  }, [targets, t])

  const submit = async () => {
    if (!isHelper && isEduWorkspace && mode === 'SCHOOL') {
      if ((schoolKind === 'SESSION' || schoolKind === 'COACH') && !schoolSessionId) {
        setError(t('NLSFieldPassSchoolErrorMissingSession'))
        return
      }
      if (schoolKind === 'EXAM' && !schoolAssessmentId.trim()) {
        setError(t('NLSFieldPassSchoolErrorMissingAssessment'))
        return
      }
      setBusy(true)
      setError(null)
      try {
        const eduScope: SchoolEduScope = {
          kind: schoolKind,
          sessionId: schoolKind === 'SESSION' || schoolKind === 'COACH' ? schoolSessionId : undefined,
          assessmentId: schoolKind === 'EXAM' ? schoolAssessmentId.trim() : undefined,
          hall: schoolKind === 'EXAM' ? schoolHall.trim() || undefined : undefined,
          metricDefIds:
            schoolKind === 'COACH' && schoolMetricsStr.trim()
              ? schoolMetricsStr
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : undefined,
        }
        const result = await createSchoolFieldPass({
          workspaceId,
          eduScope,
          expiresInHours: clampedSchoolHours,
          targetEmail: targetEmail.trim() || undefined,
        })
        const url = result.shareableUrl
        setLastLink(url)
        await copyText(url).catch(() => undefined)
        onNotice?.(t('NLSFieldPassQuickCopied'), 'success')
        await onCreated?.(
          {
            pass: { id: result.passId, expiresAt: result.expiresAt } as unknown as FieldPassRecord,
            token: result.token,
            url,
          },
          url,
        )
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : t('NLSFieldPassQuickCreateFailed')
        setError(message)
        onNotice?.(message, 'error')
      } finally {
        setBusy(false)
      }
      return
    }

    if (cleanTargets.length === 0) {
      setError(t('NLSFieldPassQuickNoTargets'))
      return
    }

    const trimmedPhone = deliveryPhone.trim()
    if (sendViaWhatsApp && !trimmedPhone) {
      setError(t('NLSFieldPassEnterWhatsAppPhone'))
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await createFieldPass({
        workspaceId,
        targetIds: cleanTargets.map((target) => target.id),
        expiresInHours,
        deleteOnCompletion,
        targetEmail: targetEmail.trim() || null,
        ...(sendViaWhatsApp && trimmedPhone ? { deliveryPhone: trimmedPhone } : {}),
      })
      if (sendViaWhatsApp && trimmedPhone) {
        const notice = t('NLSFieldPassSentWhatsApp', { phone: trimmedPhone })
        setWhatsappNotice(notice)
        setLastLink(null)
        onNotice?.(notice, 'success')
        await onCreated?.(result, '')
      } else {
        const url = buildFieldPassUrl(result.token)
        setLastLink(url)
        setWhatsappNotice(null)
        await copyText(url).catch(() => undefined)
        onNotice?.(t('NLSFieldPassQuickCopied'), 'success')
        await onCreated?.(result, url)
      }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : t('NLSFieldPassQuickCreateFailed')
      setError(message)
      onNotice?.(message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const share = async () => {
    if (!lastLink || typeof navigator === 'undefined') return
    const shareApi = (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share
    if (shareApi) {
      await shareApi({ title: t('NLSFieldPassQuickTitle'), url: lastLink }).catch(() => undefined)
      return
    }
    await copyText(lastLink).catch(() => undefined)
    onNotice?.(t('NLSFieldPassQuickCopied'), 'success')
  }

  return (
    <BottomSheet isOpen={open} title={t('NLSFieldPassQuickTitle')} onClose={() => { if (!busy) onClose() }} className="quickDelegateSheet">
        <div className="quickDelegateSheet__body">
          {!isHelper && isEduWorkspace && (
            <div className="flex gap-2 mb-3">
              <Button
                variant={mode === 'CHECKLIST' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('CHECKLIST')}
              >
                {t('NLSFieldPassModeChecklist')}
              </Button>
              <Button
                variant={mode === 'SCHOOL' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setMode('SCHOOL')}
              >
                {t('NLSFieldPassModeSchool')}
              </Button>
            </div>
          )}

          {mode === 'CHECKLIST' ? (
            <>
              <div className="quickDelegateSheet__summary">
                <LocalOfferRoundedIcon fontSize="small" />
                <div>
                  <p>{t('NLSFieldPassQuickBody')}</p>
                  <ul>
                    {cleanTargets.map((target) => <li key={target.id}>{target.title}</li>)}
                  </ul>
                </div>
              </div>
              <label>
                {t('NLSFieldPassQuickExpiresLabel')}
                <SheetSelect
                  value={String(expiresInHours) as `${number}`}
                  ariaLabel={t('NLSFieldPassQuickExpiresLabel')}
                  placeholder={t('NLSFieldPassQuickExpiresLabel')}
                  options={EXPIRY_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
                  onChange={(value) => setExpiresInHours(Number(value))}
                  disabled={busy}
                />
              </label>
              <label>
                {t('NLSFieldPassQuickTargetEmailLabel')}
                <TextInput
                  type="email"
                  value={targetEmail}
                  disabled={busy}
                  placeholder="recipient@example.com"
                  onChange={setTargetEmail}
                />
                <span>{t('NLSFieldPassQuickTargetEmailHint')}</span>
              </label>
              <Toggle
                checked={deleteOnCompletion}
                disabled={busy}
                onChange={setDeleteOnCompletion}
                label={t('NLSFieldPassQuickDeleteOnCompletion')}
              />
              <Toggle
                checked={sendViaWhatsApp}
                disabled={busy}
                onChange={setSendViaWhatsApp}
                label={t('NLSFieldPassSendWhatsApp')}
              />
              {sendViaWhatsApp ? (
                <label>
                  {t('NLSFieldPassDeliveryPhoneLabel')}
                  <TextInput
                    value={deliveryPhone}
                    disabled={busy}
                    placeholder={t('NLSFieldPassDeliveryPhonePlaceholder')}
                    onChange={setDeliveryPhone}
                    aria-label={t('NLSFieldPassDeliveryPhoneLabel')}
                  />
                </label>
              ) : null}
            </>
          ) : (
            <div className="grid gap-3">
              <p className="text-xs text-[var(--muted)]">
                {t('NLSFieldPassSchoolEndsAt', { time: schoolEndTimeStr })}
              </p>
              <div>
                <label className="block text-xs font-medium text-[var(--text)] mb-1">
                  {t('NLSFieldPassSchoolKindLabel')}
                </label>
                <div className="grid gap-2 sm:grid-cols-3">
                  <div className="rounded border border-[var(--border)] p-2">
                    <Radio
                      name="quickSchoolPassKind"
                      checked={schoolKind === 'SESSION'}
                      onChange={(checked) => {
                        if (checked) setSchoolKind('SESSION')
                      }}
                      label={t('NLSFieldPassKindSubstitute')}
                    />
                  </div>
                  <div className="rounded border border-[var(--border)] p-2">
                    <Radio
                      name="quickSchoolPassKind"
                      checked={schoolKind === 'EXAM'}
                      onChange={(checked) => {
                        if (checked) setSchoolKind('EXAM')
                      }}
                      label={t('NLSFieldPassKindExam')}
                    />
                  </div>
                  <div className="rounded border border-[var(--border)] p-2">
                    <Radio
                      name="quickSchoolPassKind"
                      checked={schoolKind === 'COACH'}
                      onChange={(checked) => {
                        if (checked) setSchoolKind('COACH')
                      }}
                      label={t('NLSFieldPassKindCoach')}
                    />
                  </div>
                </div>
              </div>

              {(schoolKind === 'SESSION' || schoolKind === 'COACH') && (
                <div>
                  <label className="block text-xs font-medium text-[var(--text)] mb-1">
                    {t('NLSFieldPassSchoolSessionSelect')}
                  </label>
                  <SheetSelect
                    value={schoolSessionId}
                    ariaLabel={t('NLSFieldPassSchoolSessionSelect')}
                    placeholder={t('NLSFieldPassSchoolSessionSelect')}
                    options={availableSeries.map((s) => ({
                      value: s.id ?? '',
                      label: s.id || `${s.classId} · ${s.startTime}`,
                    }))}
                    onChange={(v) => setSchoolSessionId(String(v))}
                    disabled={busy}
                  />
                </div>
              )}

              {schoolKind === 'EXAM' && (
                <div className="grid gap-2">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text)] mb-1">
                      {t('NLSFieldPassSchoolAssessmentSelect')}
                    </label>
                    <TextInput
                      data-testid="school-pass-assessment-input"
                      value={schoolAssessmentId}
                      onChange={setSchoolAssessmentId}
                      placeholder="e.g. assessment-midterm-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text)] mb-1">
                      {t('NLSFieldPassSchoolHallLabel')}
                    </label>
                    <TextInput
                      data-testid="school-pass-hall-input"
                      value={schoolHall}
                      onChange={setSchoolHall}
                      placeholder="e.g. Hall 3B"
                    />
                  </div>
                </div>
              )}

              {schoolKind === 'COACH' && (
                <div>
                  <label className="block text-xs font-medium text-[var(--text)] mb-1">
                    {t('NLSFieldPassSchoolMetricsLabel')}
                  </label>
                  <TextInput
                    data-testid="school-pass-metrics-input"
                    value={schoolMetricsStr}
                    onChange={setSchoolMetricsStr}
                    placeholder="Metric IDs (comma separated)"
                  />
                </div>
              )}
            </div>
          )}
          {error ? (
            <p className="quickDelegateSheet__error">{error}</p>
          ) : null}
          {whatsappNotice ? (
            <div className="quickDelegateSheet__link" data-testid="field-pass-whatsapp-notice">
              <p className="text-xs text-[var(--success)] font-medium">{whatsappNotice}</p>
            </div>
          ) : null}
          {lastLink ? (
            <div className="quickDelegateSheet__link">
              <Pressable
                className="quickDelegateSheet__linkValue"
                onClick={() => void copyText(lastLink).then(() => onNotice?.(t('NLSFieldPassQuickCopied'), 'success'))}
              >
                {lastLink}
              </Pressable>
              <div className="quickDelegateSheet__linkActions">
                <Button variant="secondary" size="sm" icon={<ContentCopyRoundedIcon fontSize="small" />} onClick={() => void copyText(lastLink).then(() => onNotice?.(t('NLSFieldPassQuickCopied'), 'success'))}>
                  {t('NLSFieldPassQuickCopyLink')}
                </Button>
                <Button variant="tertiary" size="sm" icon={<IosShareRoundedIcon fontSize="small" />} onClick={() => void share()}>
                  {t('NLSFieldPassQuickShareLink')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        <footer className="quickDelegateSheet__footer">
          <Button variant="tertiary" onClick={onClose} disabled={busy}>{t('NLSCommonActionCancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={busy || cleanTargets.length === 0} loading={busy}>{busy ? t('NLSFieldPassQuickCreating') : t('NLSFieldPassQuickCreate')}</Button>
        </footer>
    </BottomSheet>
  )
}

export default QuickDelegateSheet
